/**
 * Version History Panel
 * Shows version history, current JSON config (always expanded & editable), and allows activating old versions
 */

import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { agentApi } from '@/lib/api';

// Lazy load Monaco Editor - ~2MB, only loaded when user clicks "Edit"
const LazyEditor = React.lazy(() => import('@monaco-editor/react'));
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loading } from '@/components/common';
import { useI18n } from '@/i18n';
import { History, RotateCcw, Eye, EyeOff, Copy, Check, Download, Code, Save, AlertCircle, Zap, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import type { AgentConfigVersion, AgentConfiguration } from '@/types';
import { ConfigMigrationDialog } from './ConfigMigrationDialog';
import { toast } from 'sonner';

interface VersionHistoryProps {
  agentType: string;
  currentVersion: number;
  currentConfig?: Partial<AgentConfiguration>;
  onConfigReplace?: (newConfig: Partial<AgentConfiguration>) => void;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  agentType,
  currentVersion: _currentVersion,
  currentConfig,
  onConfigReplace,
}) => {
  const queryClient = useQueryClient();
  const { messages, language } = useI18n();
  const t = messages.agentConfig.versionHistory;
  const tj = messages.agentConfig.jsonPreview;
  const [viewingVersion, setViewingVersion] = useState<AgentConfigVersion | null>(null);
  const [copied, setCopied] = useState(false);

  // Editable JSON state
  const [editedJson, setEditedJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [hasJsonChanges, setHasJsonChanges] = useState(false);

  // Monaco editor lazy loading state
  const [isEditorActive, setIsEditorActive] = useState(false);

  // Migration state
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [pendingMigrationConfig, setPendingMigrationConfig] = useState<any | null>(null);
  const CURRENT_SCHEMA_VERSION = 2;

  // Initialize edited JSON when currentConfig changes
  useEffect(() => {
    if (currentConfig) {
      const jsonStr = JSON.stringify(currentConfig, null, 2);
      setEditedJson(jsonStr);
      setHasJsonChanges(false);
      setJsonError(null);
    }
  }, [currentConfig]);

  // Handle JSON editor changes
  const handleJsonChange = useCallback((value: string | undefined) => {
    if (value === undefined) return;
    setEditedJson(value);

    // Validate JSON
    try {
      JSON.parse(value);
      setJsonError(null);
      // Check if different from original
      const originalJson = JSON.stringify(currentConfig, null, 2);
      setHasJsonChanges(value !== originalJson);
    } catch (e) {
      setJsonError((e as Error).message);
      setHasJsonChanges(false);
    }
  }, [currentConfig]);

  // Check if config needs migration
  const needsMigration = useCallback((config: any): boolean => {
    const version = config.schema_version || 1; // No version = V1
    return version < CURRENT_SCHEMA_VERSION;
  }, []);

  // Apply migration to config
  const applyMigration = useCallback((config: any): any => {
    const migratedConfig = { ...config };

    // Apply V1 → V2 migration
    if (!migratedConfig.schema_version || migratedConfig.schema_version === 1) {
      // Set version
      migratedConfig.schema_version = 2;

      // Ensure models structure
      if (!migratedConfig.models) {
        migratedConfig.models = {};
      }

      // Migrate classifier.model_id → models.classification.model_id
      if (migratedConfig.hooks?.classifier?.model_id) {
        if (!migratedConfig.models.classification) {
          migratedConfig.models.classification = {};
        }
        migratedConfig.models.classification.model_id = migratedConfig.hooks.classifier.model_id;
        delete migratedConfig.hooks.classifier.model_id;
      }

      // Migrate classifier.quick_response_model → models.chat.quick_model_id
      if (migratedConfig.hooks?.classifier?.quick_response_model) {
        if (!migratedConfig.models.chat) {
          migratedConfig.models.chat = {};
        }
        const quickModel = migratedConfig.hooks.classifier.quick_response_model;
        migratedConfig.models.chat.quick_model_id =
          typeof quickModel === 'object' ? quickModel.model_id : quickModel;
        delete migratedConfig.hooks.classifier.quick_response_model;
      }

      // Migrate session_summary.model_id → models.memory.model_id
      if (migratedConfig.session_summary?.model_id) {
        if (!migratedConfig.models.memory) {
          migratedConfig.models.memory = {};
        }
        migratedConfig.models.memory.model_id = migratedConfig.session_summary.model_id;
        delete migratedConfig.session_summary.model_id;
      }

      // Migrate deep_response_instructions → instructions
      if (migratedConfig.hooks?.classifier?.deep_response_instructions) {
        migratedConfig.instructions = migratedConfig.hooks.classifier.deep_response_instructions;
        migratedConfig.hooks.classifier.deep_response_instructions = '';
      }
    }

    return migratedConfig;
  }, []);

  // Get migration changes for preview
  const getMigrationChanges = useCallback((config: any) => {
    const changes: any[] = [];

    if (config.hooks?.classifier?.model_id) {
      changes.push({
        type: 'modify',
        field: 'hooks.classifier.model_id → models.classification.model_id',
        description: '分类模型配置迁移到统一模型配置',
        before: config.hooks.classifier.model_id,
        after: config.hooks.classifier.model_id,
      });
    }

    if (config.hooks?.classifier?.deep_response_instructions) {
      changes.push({
        type: 'modify',
        field: 'hooks.classifier.deep_response_instructions → instructions',
        description: '深度响应提示词迁移到主提示词字段',
        before: `${config.hooks.classifier.deep_response_instructions.slice(0, 50)}...`,
        after: `${config.hooks.classifier.deep_response_instructions.slice(0, 50)}...`,
      });
    }

    if (config.session_summary?.model_id) {
      changes.push({
        type: 'modify',
        field: 'session_summary.model_id → models.memory.model_id',
        description: '会话摘要模型配置迁移',
        before: config.session_summary.model_id,
        after: config.session_summary.model_id,
      });
    }

    if (changes.length === 0) {
      changes.push({
        type: 'modify',
        field: 'schema_version',
        description: '更新配置格式版本号',
        before: '1',
        after: '2',
      });
    }

    return changes;
  }, []);

  // Handle load to editor (with migration check)
  const handleLoadToEditor = useCallback((config: any) => {
    if (needsMigration(config)) {
      // Show migration dialog
      setPendingMigrationConfig(config);
      setMigrationDialogOpen(true);
    } else {
      // Direct load - also activate editor for editing
      setEditedJson(JSON.stringify(config, null, 2));
      setHasJsonChanges(true);
      setJsonError(null);
      setIsEditorActive(true);
    }
  }, [needsMigration]);

  // Confirm migration
  const handleConfirmMigration = useCallback(() => {
    if (!pendingMigrationConfig) return;

    try {
      const migrated = applyMigration(pendingMigrationConfig);
      setEditedJson(JSON.stringify(migrated, null, 2));
      setHasJsonChanges(true);
      setJsonError(null);
      setIsEditorActive(true);
      setMigrationDialogOpen(false);
      setPendingMigrationConfig(null);

      // Show success toast
      toast.success(
        language === 'zh'
          ? '配置已升级到 v2！请保存以应用更改。'
          : 'Configuration upgraded to v2! Save to apply changes.',
        {
          duration: 5000,
        }
      );
    } catch (e) {
      toast.error(
        language === 'zh'
          ? '迁移失败：' + (e as Error).message
          : 'Migration failed: ' + (e as Error).message
      );
    }
  }, [pendingMigrationConfig, applyMigration, language]);

  // Apply JSON changes
  const handleApplyJsonChanges = useCallback(() => {
    if (jsonError || !hasJsonChanges) return;

    try {
      const parsed = JSON.parse(editedJson);
      if (onConfigReplace) {
        onConfigReplace(parsed);
        setHasJsonChanges(false);
      }
    } catch (e) {
      setJsonError((e as Error).message);
    }
  }, [editedJson, jsonError, hasJsonChanges, onConfigReplace]);

  // Reset JSON to original
  const handleResetJson = useCallback(() => {
    if (currentConfig) {
      const jsonStr = JSON.stringify(currentConfig, null, 2);
      setEditedJson(jsonStr);
      setHasJsonChanges(false);
      setJsonError(null);
    }
  }, [currentConfig]);

  // Pagination state
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);

  // Fetch versions (paginated)
  const { data: versionsData, isLoading, isFetching } = useQuery({
    queryKey: ['agent-versions', agentType, page],
    queryFn: () => agentApi.getVersions(agentType, page, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });

  const versions = versionsData?.items;
  const totalPages = versionsData?.total_pages ?? 1;
  const totalVersions = versionsData?.total ?? 0;

  // Activate version mutation
  const activateMutation = useMutation({
    mutationFn: ({ version, reason }: { version: number; reason?: string }) =>
      agentApi.activateVersion(agentType, version, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-config', agentType] });
      queryClient.invalidateQueries({ queryKey: ['agent-versions', agentType] });
      queryClient.invalidateQueries({ queryKey: ['agents-list'] });
    },
  });

  const handleActivate = (version: number) => {
    if (confirm(t.confirmRestore.replace('{version}', String(version)))) {
      activateMutation.mutate({ version, reason: t.restoreReason });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US');
  };

  // JSON copy/download helpers
  const handleCopy = async (config: any) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(config, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = (config: any, version?: number) => {
    const jsonString = JSON.stringify(config, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = version
      ? `${agentType}-v${version}-config.json`
      : `${agentType}-config.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <Loading message={t.loading} />;
  }

  return (
    <div className="space-y-4">
      {/* Current Configuration JSON Preview - Always Expanded & Editable */}
      {currentConfig && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Code className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-medium text-gray-900">{tj?.title || 'JSON配置'}</h3>
              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">
                {t.editing || '编辑中'}
              </span>
              {hasJsonChanges && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                  {tj?.modified || 'Modified'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasJsonChanges && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResetJson}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    {tj?.reset || 'Reset'}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleApplyJsonChanges}
                    disabled={!!jsonError}
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {tj?.applyChanges || 'Apply Changes'}
                  </Button>
                </>
              )}
              <Button variant="secondary" size="sm" onClick={() => handleCopy(currentConfig)}>
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? tj?.copied || '已复制' : tj?.copy || '复制'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => handleDownload(currentConfig)}>
                <Download className="w-4 h-4 mr-1" />
                {tj?.download || '下载'}
              </Button>
            </div>
          </div>

          {/* JSON Error Display */}
          {jsonError && (
            <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="font-mono text-xs">{jsonError}</span>
            </div>
          )}

          {/* JSON View / Editor */}
          {isEditorActive ? (
            <>
              <Suspense
                fallback={
                  <div className="h-[400px] border border-gray-300 rounded-lg bg-gray-900 flex items-center justify-center">
                    <Loading message={language === 'zh' ? '加载编辑器...' : 'Loading editor...'} />
                  </div>
                }
              >
                <div className="h-[400px] border border-gray-300 rounded-lg overflow-hidden">
                  <LazyEditor
                    height="100%"
                    defaultLanguage="json"
                    value={editedJson}
                    onChange={handleJsonChange}
                    options={{
                      minimap: { enabled: false },
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      fontSize: 13,
                      tabSize: 2,
                      wordWrap: 'on',
                      automaticLayout: true,
                    }}
                    theme="vs-dark"
                  />
                </div>
              </Suspense>
              <p className="text-xs text-gray-500 mt-2">
                {tj?.editHint || 'Edit JSON directly and click "Apply Changes" to update configuration'}
              </p>
            </>
          ) : (
            <div className="relative group">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-[400px] text-xs font-mono leading-relaxed">
                <code>{editedJson}</code>
              </pre>
              <button
                onClick={() => setIsEditorActive(true)}
                className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-md border border-gray-600 transition-colors opacity-0 group-hover:opacity-100"
              >
                <Pencil className="w-3.5 h-3.5" />
                {language === 'zh' ? '编辑' : 'Edit'}
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Version History */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">{t.title}</h3>
        </div>

        {versions && versions.length > 0 ? (
          <>
            <div className={`space-y-2 ${isFetching ? 'opacity-60' : ''}`}>
              {versions.map((version) => (
                <div
                  key={version.version}
                  className={`p-3 rounded-lg border ${
                    version.is_active
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">
                        v{version.version}
                      </span>
                      {version.is_active && (
                        <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                          {messages.agentConfig.active}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingVersion(viewingVersion?.version === version.version ? null : version)}
                        title={t.viewConfig || '查看配置'}
                      >
                        {viewingVersion?.version === version.version ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      {!version.is_active && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleActivate(version.version)}
                          disabled={activateMutation.isPending}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          {t.restore}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    <span>{formatDate(version.created_at)}</span>
                    {version.description && (
                      <span className="ml-2">- {version.description}</span>
                    )}
                  </div>
                  {version.created_by && (
                    <div className="mt-1 text-xs text-gray-400">
                      {t.createdBy}: {version.created_by}
                    </div>
                  )}

                  {/* Inline JSON Preview for this version */}
                  {viewingVersion?.version === version.version && version.configuration && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {t.versionConfig?.replace('{version}', String(version.version)) || `v${version.version} 配置`}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(version.configuration)}
                          >
                            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(version.configuration, version.version)}
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          {/* Apply this version's config to editor */}
                          {onConfigReplace && !version.is_active && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                if (version.configuration) {
                                  handleLoadToEditor(version.configuration);
                                }
                              }}
                            >
                              {needsMigration(version.configuration || {}) && (
                                <Zap className="w-3 h-3 mr-1 text-amber-600" />
                              )}
                              {tj?.loadToEditor || 'Load to Editor'}
                            </Button>
                          )}
                        </div>
                      </div>
                      <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-auto max-h-[300px] text-xs font-mono">
                        <code>{JSON.stringify(version.configuration, null, 2)}</code>
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4">
                <span className="text-sm text-gray-500">
                  {language === 'zh'
                    ? `共 ${totalVersions} 个版本`
                    : `${totalVersions} versions total`}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-700 min-w-[80px] text-center">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-center py-4">{t.noHistory}</p>
        )}
      </Card>

      {/* Migration Dialog */}
      {pendingMigrationConfig && (
        <ConfigMigrationDialog
          isOpen={migrationDialogOpen}
          fromVersion={pendingMigrationConfig.schema_version || 1}
          toVersion={CURRENT_SCHEMA_VERSION}
          changes={getMigrationChanges(pendingMigrationConfig)}
          onConfirm={handleConfirmMigration}
          onCancel={() => {
            setMigrationDialogOpen(false);
            setPendingMigrationConfig(null);
          }}
        />
      )}
    </div>
  );
};

export default VersionHistory;
